// JOGI KAPU — tiszta döntési logika (CLAUDE.md 10. szabály).
// Terv és döntések: ClickUp 123qmzqzk1r (2026-10-01-én jóváhagyva, J1-J12).
//
// ⚠️ A LEGFONTOSABB SZERKEZETI DÖNTÉS: EBBEN A FÜGGVÉNYBEN NINCS
// "A KLIENS ÁLTAL ISMERT VERZIÓ".
// A Store-ból telepített natív kliens a beküldés napján BEFAGY (a becsomagolt
// webréteg statikus snapshot — ld. ios/1.0-18-web tag). Ha a kapu egy
// bundle-konstanst hasonlítana a confighoz, egy új dokumentum-verzió azon a
// kliensen SOHA nem derülne ki. Ezért az igazság kizárólag a Firestore
// configból és a nyugtákból jön; a bundle semmit nem tud a verziókról.
// Ezt teszt is védi.
//
// A MÁSODIK: ISMERETLEN ÉRTÉK = KIHAGYÁS, NEM HIBA.
// Egy későbbi dokumentum-típus vagy kapu-fajta (amit a kiadott app nem
// ismer) nem zárhatja ki véglegesen a felhasználót. Ezért az ismeretlen
// `docType` és az ismeretlen `gate` csendben kimarad — a kapu konzervatív
// irányba hibázik (átenged), nem kizáró irányba.

import { daysBetween } from "./legalDate";

export type LegalDocType = "privacy" | "terms";
export type LegalGateKind = "acknowledge" | "accept";

/** A `legalAcks` megőrzési idejének és a nyugta-kulcsnak a formája.
 * ⚠️ A kulcs SZÁNDÉKOSAN nem tartalmaz uid-ot: ez a modul tiszta, és nem
 * kap azonosítót (CLAUDE.md 1. szabály). A Firestore doksi-ID-ben szerepel
 * a uid, itt csak `docType:version`. */
export function ackKey(docType: string, version: string): string {
  return `${docType}:${version}`;
}

export const LEGAL_DOC_TYPES: readonly LegalDocType[] = ["privacy", "terms"] as const;
export const LEGAL_GATE_KINDS: readonly LegalGateKind[] = ["acknowledge", "accept"] as const;

/** Hány nappal a hatálybalépés előtt NEM zárható el többé a banner (§9). */
export const BANNER_ALWAYS_VISIBLE_DAYS = 3;
/** Meddig tart egy banner-elzárás (§9). */
export const BANNER_DISMISS_DAYS = 7;

/** Egy dokumentum config-bejegyzése az `appConfig/legal`-ból.
 * A `gate` és a kulcs SZÁNDÉKOSAN `string`: a nyers configot tipizáljuk,
 * nem a kívánságot — az érvényesítés a `resolveLegalGate` dolga. */
export interface LegalDocConfig {
  version?: string;
  announceFrom?: string;
  effectiveFrom?: string;
  url?: string;
  gate?: string;
  summary?: string;
}

/** Az `appConfig/legal` dokumentum. Ismeretlen kulcsokat is tartalmazhat. */
export type LegalConfig = Record<string, LegalDocConfig | undefined> | null | undefined;

export interface LegalGateItem {
  docType: LegalDocType;
  version: string;
  gate: LegalGateKind;
  url: string;
  summary: string;
  /** A hatálybalépés napja — a megjelenített dátum EBBŐL renderelődik (J8). */
  effectiveFrom: string;
}

export interface LegalBannerItem {
  docType: LegalDocType;
  version: string;
  url: string;
  summary: string;
  effectiveFrom: string;
  /** Hány nap van még a hatálybalépésig (0 = ma lép hatályba). */
  daysUntilEffective: number;
}

export interface LegalGateResult {
  gate: LegalGateItem[];
  banner: LegalBannerItem[];
}

function isKnownGate(v: string | undefined): v is LegalGateKind {
  return !!v && (LEGAL_GATE_KINDS as readonly string[]).includes(v);
}

/**
 * Melyik dokumentum kér nyugtát MOST, és melyikről kell bannert mutatni.
 *
 * @param config  az `appConfig/legal` tartalma — `null` is érvényes bemenet
 * @param acks    a felhasználó nyugtái, `ackKey(docType, version)` kulccsal
 * @param today   "YYYY-MM-DD", a HELYI naptári nap (`localDateString`)
 *
 * ⚠️ FAIL-OPEN: hiányzó vagy üres config esetén nincs kapu és nincs banner.
 * Precedens ugyanebben a kapu-létrában: az `App.tsx` a profil-olvasás
 * hibaágán `setOnboardingDone(true)`-t hív. Egy config-olvasási hiba miatt
 * senkit nem zárunk ki az appból — az audit-veszteség késés, nem adatvesztés.
 */
export function resolveLegalGate(
  config: LegalConfig,
  acks: Record<string, boolean>,
  today: string
): LegalGateResult {
  const gate: LegalGateItem[] = [];
  const banner: LegalBannerItem[] = [];
  if (!config) return { gate, banner };

  // A bejárás a KANONIKUS LISTÁN megy, nem a config kulcsain. Ez két dolgot
  // ad egyszerre: (1) a modál két tétele mindig ugyanabban a sorrendben
  // jelenik meg, és a tesztek sem a JSON-kulcsok rendezésén állnak;
  // (2) az ISMERETLEN docType ezzel SZERKEZETILEG kimarad — nincs szükség
  // külön ellenőrzésre, mert amit nem ismerünk, azt meg sem kérdezzük.
  // (Egy korábbi változatban volt egy isKnownDocType() szűrő is: halott kód,
  // a CI eslint-je ki is buktatta.)
  for (const docType of LEGAL_DOC_TYPES) {
    const doc = config[docType];
    if (!doc) continue;

    const version = (doc.version || "").trim();
    const effectiveFrom = (doc.effectiveFrom || "").trim();
    const announceFrom = (doc.announceFrom || "").trim();
    if (!version || !effectiveFrom) continue;      // félkész bejegyzés → kihagy
    if (!isKnownGate(doc.gate)) continue;          // ismeretlen kapu-fajta → kihagy

    const url = doc.url || "";
    const summary = doc.summary || "";

    if (today >= effectiveFrom) {
      // Hatályos. Nyugta KELL, ha erre a PONTOS verzióra még nincs.
      // ⚠️ Korábbi verzióra adott nyugta NEM elég — ezért egyenlőség-
      // vizsgálat, nem "van-e bármilyen nyugta".
      if (!acks[ackKey(docType, version)]) {
        gate.push({ docType, version, gate: doc.gate, url, summary, effectiveFrom });
      }
      continue;
    }

    // Még nem hatályos → banner, ha a bejelentési ív elindult.
    if (announceFrom && today >= announceFrom) {
      const daysUntilEffective = daysBetween(today, effectiveFrom);
      if (daysUntilEffective === null) continue;
      banner.push({ docType, version, url, summary, effectiveFrom, daysUntilEffective });
    }
  }

  return { gate, banner };
}

/**
 * Látszik-e a banner, figyelembe véve a felhasználó elzárását (§9, J10).
 *
 * @param dismissedOn  "YYYY-MM-DD", amikor elzárta — `null`, ha nem zárta el
 *                     (vagy ha a `localStorage` olvasása dobott)
 *
 * ⚠️ AZ UTOLSÓ 3 NAPBAN MINDIG LÁTSZIK, az elzárás ellenére is. Indok: aki
 * az ív 1. napján becsukja, ne "váratlanul" kapja a blokkoló ablakot a 30.
 * napon. Az elzárás kényelmi funkció, nem jog arra, hogy ne tájékoztassuk.
 *
 * ⚠️ A `localStorage` dobása esetén a hívó `null`-t ad, és a banner LÁTSZIK
 * — a hiba nem veheti el a tájékoztatást.
 */
export function shouldShowBanner(
  item: Pick<LegalBannerItem, "daysUntilEffective">,
  dismissedOn: string | null,
  today: string
): boolean {
  if (item.daysUntilEffective <= BANNER_ALWAYS_VISIBLE_DAYS) return true;
  if (!dismissedOn) return true;
  const elapsed = daysBetween(dismissedOn, today);
  // Értelmezhetetlen vagy JÖVŐBELI elzárás-dátum (átállított óra, kézzel
  // szerkesztett localStorage) → mutatjuk. A banner elrejtése a sérülékeny
  // irány; a megjelenítése legfeljebb kellemetlen.
  if (elapsed === null || elapsed < 0) return true;
  return elapsed >= BANNER_DISMISS_DAYS;
}
